import React, { createContext, useState, useEffect, useContext } from 'react';
import { RadixDappToolkit, RadixNetwork, DataRequestBuilder } from '@radixdlt/radix-dapp-toolkit';

// Create the context
export const RadixConnectContext = createContext(null);

// Hook to use the Radix Connect context
export const useRadixConnect = () => {
  const context = useContext(RadixConnectContext);
  if (!context) {
    throw new Error('useRadixConnect must be used within a RadixConnectProvider');
  }
  return context;
};

// Provider component
export const RadixConnectProvider = ({ children, dAppDefinitionAddress }) => {
  const [rdt, setRdt] = useState(null);
  const [connected, setConnected] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // Initialize the Radix dApp Toolkit
  useEffect(() => {
    const toolkit = RadixDappToolkit({
      dAppDefinitionAddress,
      networkId: RadixNetwork.Mainnet,
      applicationName: 'Reddicks Games',
      applicationVersion: '1.0.0',
    });

    // Set the button theme to dark mode
    toolkit.buttonApi.setTheme('black');
    toolkit.buttonApi.setMode('dark');

    // Configure the data request to include accounts
    toolkit.walletApi.setRequestData(
      DataRequestBuilder.accounts().atLeast(1)
    );

    // Handle connect responses with improved error logging
    toolkit.walletApi.provideConnectResponseCallback((result) => {
      if (result.isErr()) {
        console.error('Connect error:', result.error);
      } else {
        console.log('Connect success with data:', result.value);
        setConnected(true);
      }
    });

    // Log when toolkit is initialized
    setRdt(toolkit);

    // Clean up on unmount
    return () => {
      toolkit.destroy();
    };
  }, [dAppDefinitionAddress]);

  // Handle connect/disconnect events
  useEffect(() => {
    if (!rdt) return;

    const handleConnect = () => {
      console.log('Connected to Radix wallet');
      setConnected(true);
    };

    const handleDisconnect = () => {
      console.log('Disconnected from Radix wallet');
      setConnected(false);
      setAccounts([]);
      setSelectedAccount(null);
    };

    // Add event listeners
    document.addEventListener('radix-connect-button:onConnect', handleConnect);
    document.addEventListener('radix-connect-button:onDisconnect', handleDisconnect);

    // Check if the button is already in a connected state
    setTimeout(() => {
      const connectButton = document.querySelector('radix-connect-button');
      if (connectButton && connectButton.getAttribute('state') === 'connected') {
        console.log('Button is already in connected state');
        setConnected(true);
      } else {
        console.log('Button state:', connectButton ? connectButton.getAttribute('state') : 'button not found');
      }
    }, 500);

    // Subscribe to wallet data changes with better logging
    const subscription = rdt.walletApi.walletData$.subscribe((walletData) => {
      // console.log('Wallet data received:', walletData ? 'data present' : 'no data'); // REMOVED
      
      // Check if we're connected based on wallet data
      if (walletData && walletData.accounts) {
        // console.log('Accounts found:', walletData.accounts.length); // REMOVED
        setConnected(true);
        setAccounts(walletData.accounts);
        
        // Set the first account as selected if we have accounts and none is selected
        if (walletData.accounts.length > 0 && !selectedAccount) {
          setSelectedAccount(walletData.accounts[0]);
          console.log('Selected account:', walletData.accounts[0].address);
        }
      } else {
        console.log('No accounts found in wallet data');
      }
    });

    // Check initial connection state
    const initialWalletData = rdt.walletApi.getWalletData();
    if (initialWalletData && initialWalletData.accounts && initialWalletData.accounts.length > 0) {
      // console.log('Initial wallet data has accounts:', initialWalletData.accounts.length); // REMOVED
      setConnected(true);
      setAccounts(initialWalletData.accounts);
      setSelectedAccount(initialWalletData.accounts[0]);
    } else {
      console.log('No accounts in initial wallet data:', initialWalletData);
    }

    return () => {
      document.removeEventListener('radix-connect-button:onConnect', handleConnect);
      document.removeEventListener('radix-connect-button:onDisconnect', handleDisconnect);
      subscription.unsubscribe();
    };
  }, [rdt, selectedAccount]);

  // Function to request account sharing update
  const updateAccountSharing = async () => {
    if (!rdt) return;
    
    try {
      const result = await rdt.walletApi.sendRequest();
      if (result.isErr()) {
        console.error('Error updating account sharing:', result.error);
        return;
      }
      console.log('Account sharing updated:', result.value);
    } catch (error) {
      console.error('Error sending request:', error);
    }
  };

  // Context value
  const contextValue = {
    rdt,
    connected,
    accounts,
    selectedAccount,
    setSelectedAccount,
    updateAccountSharing
  };

  return (
    <RadixConnectContext.Provider value={contextValue}>
      {children}
    </RadixConnectContext.Provider>
  );
}; 